import React from "react";
import {
  RoomOutsideType,
  RoomPlanType,
  RoomSizeUnit,
  SmokingType,
} from "src/helper/enum";

export interface SearchObjectItem {
  key: string;
  query: string;
  listKeyOneItem?: string[];
  m_key: string;
}

interface IUser {
  id: number;
  name: string;
  email: string;
  roles: string[];
  user_type: string;
}

interface IAuth {
  user: IUser | undefined;
  access_token: string;
  loading: boolean;
  user_type: string;
}

interface IPermissionsResult {
  isAllow: boolean;
  callback?: (user: IUser) => any;
}

interface IRoute {
  path: string;
  element?: React.ReactNode;
  children?: IRoute[];
  permissions?: (user: IUser, callback?: () => void) => IPermissionsResult;
  layout?: React.ReactNode;
}

const ROLES = {
  ADMIN: "admin",
  USER_NORMAL: "user-normal",
};

interface IAmenities {
  meta_value_id: string;
  pk: string;
  sort: string;
}

interface INotice {
  title: string;
  content: string;
}

export interface RoomSize {
  size?: number;
  unit?: RoomSizeUnit;
  outside?: {
    size?: number;
    type?: RoomOutsideType;
    unit?: RoomSizeUnit;
  };
}
export interface BedSize {
  length: number;
  width: number;
}
interface RoomModel {
  sort: string;
  hotel_logo?: string;
  logo?: string;
  images: Image[];
  amenities: {
    value: string;
    label: string;
  }[];
  facilities: {
    value: string;
    label: string;
  }[];
  check_in_time_from: string;
  check_in_time_to: string;
  check_out_time: string;
  meter_from: number;
  renewal_date: string;
  floor_number_bottom: number;
  floor_number_top: number;
  floor_plan: string;
  price: number;
  discount_price: number;
  discount_amount: number;
  point_room_plan: number;
  total_plans: number;
  price_v3?: IPriceV3;
  room_id?: number;
  room_name: string;
  name?: string;
  capacity_max?: number;
  capacity_min?: number;
  hotel_id?: string;
  plans: PlanModel[];
  tags?: IRoomTag[];
  room_description?: string;
  room_type?: RoomPlanType;
  room_style?: string;
  smoking_type?: SmokingType;
  room_size?: RoomSize;
  bed_size?: BedSize;
  views?: {
    value: string;
    label: string;
  }[];
  cheapest_price?: number;
  total_booking?: number;
  created_at?: string;
  updated_at?: string;
  pk?: string;
  children_include_capacity_A?: number;
  children_include_capacity_B?: number;
  children_text_description_A?: string;
  children_text_description_B?: string;
  children_calc_price_type_A: number;
  children_calc_price_type_B: number;
}

export interface TabContentModel {
  title?: string;
  content?: string;
  image?: string;
  sort?: string;
  tab_id?: string;
  id?: string;
}

interface IHotelIntroduction {
  sort?: string;
  id?: string;
  tab_name?: string;
  hotel_id?: string;
  contents?: TabContentModel[];
}

interface IAccessInfo {
  address: string;
  description: string;
  lat: number;
  long: number;
  parking_info: IParking_info | any;
  phone_number: string;
  pickup_info: {
    "あり(無料)": string;
    [key: string]: string;
  };
}

interface IParking_info {
  バレーサービス: string;
  料金: string;
  駐車スペース: string;
  駐車場台数: string;
  駐車時間: string;
  [key: string]: string;
}

interface IMetaItem {
  m_key: string;
  sort: string;
  text_value: string;
  checked?: boolean;
  childen: IMetaItem[];
  root_path?: string;
  count?: string;
}

interface IMetaItemDynamoDb {
  m_key: string;
  sort: string;
  text_value: string;
  text_value_en?: string;
  order?: number;
  value?: string;
}

interface IMetaItemPopularFilter {
  m_key: string;
  sort: string;
  text_value: string;
  root_path: string;
  order: number;
}

interface IMetaItemPopularKeyword {
  m_key: string;
  sort: string;
  text_value: string;
  root_path: string;
  order: number;
  type: string;
}

interface IMessage {
  image: string;
  user_id: string;
  text: string;
}

interface INftData {
  date: string;
  image_url: string;
  reservation_number: string;
  status: string;
}
interface ReservationDetailModel {
  room_name: string;
  room_logo?: string;
  hotel_logo?: string;
  plan_name?: string;
  description: string;
  start_date: number;
  end_date: number;
  tags?: IRoomTag[];
  hotel_name: string;
  hotel_kana_name: string;
  hotel_address: string;
  hotel_post_code: string;
  hotel_address_obj: {
    zone?: string;
    zone_id?: string;
    area?: string;
    area_id?: string;
    city?: string;
    city_id?: string;
    address_detail?: string;
  };
  hotel_tel: string;
  hotel_id: string;
  messages?: IMessage[];
  price?: number;
  total_price?: number;
  hotel_tax?: string;
  room_images?: Image[];
  status: EReservationStatus;
  last_resale_order: IResaleHotelModel;
  nft: INftData | null;
  room_info_person: { adult: number; childrens: number }[];
  seller_id: string | null;
  user_review?: UserReview | null;
  reservation_id: string;
  seller_review?: SellerReview | null;
  sort?: string;
  hotel_types?: { label: string }[];
  plan_type?: number;
  original_reservation?: string;
  is_splited?: boolean;
}

interface SellerReview {
  content: string;
  type: number;
}

interface UserReview {
  amenity: number;
  bath: number;
  comment: string;
  created_at: string;
  customer_service: number;
  facilities: number;
  hotel_id: string;
  pk: string;
  review_status: UserReviewStatus;
  satisfaction: number;
  sort: string;
  updated_at: string;
  user_id: string;
  booker?: {
    full_name?: string;
  };
  booking?: {
    room_name?: string;
    start_date?: number;
    rooms?: RoomInfoPerson[];
  };
  reply?: string;
}

enum UserReviewStatus {
  PENDDING = "PENNDING",
  SUCCESS = "SUCCESS",
}

interface ReservationListitemModel {
  sort: string;
  reservation_id: string;
  reservation_type: string;
  hotel_name: string;
  room_name: string;
  start_date: string;
  end_date: string;
  rooms: IOrderRoomDetail[];
}

interface IReview {
  comment: string;
  date: string;
  point: number;
}
export interface Image {
  alt: string;
  url: string;
}

export interface IPriceV3 {
  day: string;
  people_adult: number;
  people_children: number;
  people_count: number;
  percent_adult: number;
  percent_children: number;
  plan: {
    name: string;
    plan_id: number;
  };
  point_room_plan: number;
  price_discount: number;
  price_no_discount: number;
  price_origin: number;
  room: {
    name: string;
    room_id: number;
  };
  room_count: number;
}

export interface IHotelService {
  start_hours: number;
  start_minute: number;
  end_hours: number;
  end_minute: number;
  out_door_price?: string;
  out_door_fee?: string;
  in_door_price?: string;
  in_door_fee?: string;
  gym_price?: string;
  gym_fee?: string;
  description: string;
}

interface HotelModel {
  access_info?: IAccessInfo;
  access_guide?: string;
  hotel_id?: string;
  post_code?: string;
  telephone_number?: string;
  logo?: string;
  images?: Image[];
  name?: string;
  title?: string;
  rooms?: RoomModel[];
  plans?: PlanModel[];
  rating?: number;
  region_id?: string;
  station_id?: string;
  province_id?: string;
  kana_name?: string;
  introduction?: IHotelIntroduction[];
  check_in_time_from?: string;
  check_in_time_to?: string;
  check_out_time?: string;
  description?: string;
  tax?: string;
  amenities?: IAmenities[];
  facility_info?: string;
  hotel_type?: { key?: string; value?: string };
  min_price?: number;
  discount_price?: number;
  price_v3?: IPriceV3;
  address_1?: string;
  hotel_zone_text?: {
    zone?: string;
    area?: string;
    city?: string;
  };
  address_2?: string;
  notice?: INotice[];
  reviews?: UserReview[];
  plan_count?: number;
  geo_lat?: number;
  geo_long?: number;
  zone?: string;
  area?: string;
  city?: string;
  count_rooms?: number;
  hotel_ranking?: number;
  hotel_rating_avg?: number;
  hotel_rating_total?: number;
  hotel_rating_count?: number;
  address?: {
    address_detail: string;
    zone: string;
    area: string;
    city: string;
  };
  card_info?: {
    label?: string;
    value?: string;
  }[];
  bath_hot_spring?: {
    label?: string;
    value?: string;
  }[];
  pet?: boolean;
  child?: boolean;
  child_description?: string;
  pick_up_information?: string;
  massage_service?: string;
  room_service?: boolean;
  is_out_door?: boolean;
  is_in_door?: boolean;
  is_gym?: boolean;
  is_massage_service?: boolean;
  in_door?: IHotelService;
  out_door?: IHotelService;
  gym?: IHotelService;
  room_service_description?: string;
  late_check_out?: string;
  parking_information?: {
    parking_price?: string;
    parking_time?: string;
    parking_space?: string;
    number_of_parking_lots?: string;
    valet_service?: string;
    description?: string;
    start_hours?: number;
    start_minute?: number;
    end_hours?: number;
    end_minute?: number;
    is_setting_time?: boolean;
  };
  lat?: number;
  lng?: number;
  long?: number;
}

interface PlanModel {
  sort: string;
  plan_id: string;
  use_check_in_out: boolean;
  amenities: IAmenities[];
  bookable_end: string;
  bookable_start: string;
  check_in_time_from: string;
  check_in_time_to: string;
  check_out_time: string;
  hotel_id: string;
  images: Image[];
  member_rank: string;
  name: string;
  note?: string;
  rooms: RoomModel[];
  price: PriceModel;
  description: string;
  price_v3?: IPriceV3;
  total_rooms: number;
  plan_type: number;
}

interface PriceModel {
  date: string;
  discount_price: number;
  go_to_coupon_price: number;
  go_to_period: number;
  go_to_rate: number;
  hotel_id: string;
  instant_point: number;
  instant_point_rate: number;
  inventory: number;
  is_upper_go_to_coupon: string;
  plan_id: string;
  point: number;
  point_rate: number;
  pref_coupon_price: number;
  price: number;
  room_id: string;
  uid: string;
}

interface NotificationModel {
  title: string;
  content: string;
}

interface IRoomTag {
  tag_name: string;
  tag_value: string;
}
interface RoomDetailHotelSevervationModel {
  hotel_name: string;
  hotel_address: string;
  room_name: string;
  hotel_logo: string;
  tags?: IRoomTag[];
  description: string;
  images?: string[];
}

interface ReSaleDetailModel {
  image: string;
  hotel_name: string;
  purchase_schedule: string;
  room_type: string;
  accommodation_plan_name: string;
  number_of_rooms: number;
  number_of_guest: number;
}

interface IOrderRoomDetail {
  adult?: number;
  childrens?: number;
}
interface OrderDetailModel {
  price?: number;
  total_price?: number;
  date?: string;
  rooms: IOrderRoomDetail[];
  point_discount?: number;
  point_room_plan?: number;
  coupon_discount?: number;
  start_date?: string;
  end_date?: string;
  use_point?: number;
}

interface FormHotelReservationModel {
  username: string;
  full_name_furigana: string;
  email: string;
  postal_code: number;
  address: string;
  address_other_1: string;
  scheduled_check_in_time: string;
  accommodation_representative_name: boolean;
  full_name: string;
  full_name_2: string;
  address_2: string;
  address_other_2: string;
  postal_code_2: number;
  phone_number: number;
  phone_number_2: number;
  number_guest_adults: string;
  number_guest_child: string;
  rooms: RoomInfoPerson[];
  use_point: string;
  use_point_discount: string;
  use_wallet_price: number | undefined | null;
  show_card: boolean;
  use_card_option: string;
  card_number: number;
  card_cvv: number;
  card_month: any;
  card_year: any;
  card_holder_name: string;
  token_card: string;
  use_point_custom: number | undefined | null;
}

interface FormUserProfile {
  full_name: string;
  full_name_furigana: string;
  postal_code: number;
  email: string;
  address: string;
  address_other_1: string;
  phone_number: number;
}

interface FormChangePasswordData {
  old_password: string;
  new_password: string;
  new_password_confirm: string;
}

interface FormCardPaymentReservationModel {
  card_number: string;
  security_code: string;
  month_expire: string;
  year_expire: string;
  card_holder: string;
}

interface FormChangeQuantityGuest {
  number_guest_adults: string;
  number_guest_child: string;
  total_room: string;
}
interface FormToSaleModel {
  title: string;
  description: string;
  price: number;
  date_start: string | any;
  date_end: string | any;
  deadline: any;
}

interface IColumn {
  title: string | React.ReactNode;
  dataIndex?: string;
  width?: string;
  alignCenter?: boolean;
  style?: React.CSSProperties;
  tdCss?: React.CSSProperties | ((row: any) => React.CSSProperties);
  render?: (row: any, index: number) => React.ReactNode;
}

interface IStation {
  station_id: string;
  station_name: string;
  checked?: boolean;
}

interface IProvince {
  province_id: string;
  province_name: string;
  stations: IStation[];
  checked?: boolean;
}

interface IRegion {
  region_id: string;
  region_name: string;
  provinces: IProvince[];
  checked?: boolean;
}

interface IHotelType {
  filter_type: string;
  is_popular: boolean;
  name: string;
  value: string;
}

enum IStatusCode {
  OK = "OK",
  INTERNAL_ERROR = "INTERNAL_ERROR",
}

interface IServerResponse {
  data: { [key: string]: string };
  error_message: string;
  request_id: string;
  status_code: IStatusCode;
}

enum ELanguage {
  JA = "ja",
  EN = "en",
}

enum EResaleOrderStatus {
  selling = "0",
  sold = "10",
  expired = "3",
}

enum EReservationStatus {
  available = "available",
  selling = "selling",
  sold = "sold",
  canceled = "canceled",
  splitted = "splitted",
}

enum ESortBy {
  DESC = "desc",
  ASC = "asc",
}

// 1- payment wallet + card + point
// 2- payment wallet + card
// 3- payment card + point
// 4- payment wallet + point
// 5- payment wallet
// 6- payment card
// 7- payment point
enum FLOW_PAYMENT_ENUM {
  flow_1 = 1,
  flow_2 = 2,
  flow_3 = 3,
  flow_4 = 4,
  flow_5 = 5,
  flow_6 = 6,
  flow_7 = 7,
}

enum ELocalStorageKey {
  keyword_history = "keyword_history_v2",
}

interface IResaleHotelItem {
  title: string;
  hotel_logo: string;
  hotel_name: string;
  deadline_date: Date | string;
  check_in_start_date: Date | string;
  amount: number;
  str_date: string;
  hotel_id: string;
  resale_id: string;
  address_1?: string;
  address_2?: string;
  images?: { url: string; alt: string }[];
  isBuyed?: boolean;
}

interface ICreatedBy {
  address?: {
    address_line: string;
    postal_code: number;
    prefecture_id: string;
  };
  email: string;
  full_name: string;
  name: string;
  kana_name: string;
  phone_number: string;
  user_id: string;
  reservation_profile: {
    reservation_avatar: string;
    reservation_name: string;
  };
  resale_profile: {
    resale_avatar: string;
    resale_name: string;
    resale_description: string;
  };
  buy_profile: {
    buy_avatar: string;
    buy_name: string;
    buy_description: string;
  };
  reviews?: any[];
}

export interface IDataCalendar {
  [key: string]: {
    room: number;
    price: number;
  };
}

export enum IDataOrderStatus {
  SUCCESS = 0,
  FAIL = 1,
  PENDING = 2,
}

export enum IDataOrderType {
  RESERVATION = 1,
  WITHDRAW = 2,
  RESALE = 3,
}

export enum IDataPaymentType {
  CARD = 1,
  WALLET = 2,
  CARD_WALLET = 3,
}

export interface IDataOrderHistory {
  created_at: number;
  hotel_id: string;
  hotel_name?: string;
  room_id: string;
  room_name: string;
  plan_id: string;
  plan_name: string;
  start_date: number;
  end_date: number;
  status: IDataOrderStatus;
  log_reservation_type: IDataOrderType;
  payment_type: IDataPaymentType;
  total_price: number;
  commission_price?: number;
  buy_price?: number;
  withdraw_bank_info?: FormInfoBankUser;
  price_type: number;
  price_in: boolean;
}

interface IResaleReservation {
  sort: string;
  booker: {
    email: string;
    full_name: string;
    kana_name: string;
    phone_number: string;
  };
  booker_address: {
    address_line: string;
    postal_code: number;
    prefecture_id: string;
  };
  coupon_discount: number;
  description: string;
  end_date: number;
  estimated_check_in: string;
  hotel_address: string;
  hotel_id: string;
  hotel_name: string;
  hotel_tel: string;
  plan_id: string;
  point_discount: string;
  price: number;
  representative: {
    full_name: string;
    kana_name: string;
    phone_number: string;
  };
  representative_address: {
    address_line: string;
    postal_code: number;
    prefecture_id: string;
  };
  reservation_id: string;
  reservation_type: string;
  room_id: string;
  room_name: string;
  rooms: { adult: number; childrens: number }[];
  start_date: number;
  total_price: number;
  user_id: string;
}

interface IResaleHotelModel {
  pk: string;
  sort: string;
  created_by: ICreatedBy;
  description: string;
  end_date: number;
  expiration_date: string;
  gsi_hash: string;
  hotel: HotelModel;
  hotel_rating: number;
  order_id: string;
  price: number;
  reservation: IResaleReservation;
  reservation_id: string;
  room: RoomModel;
  sort_key: string;
  start_date: number;
  title: string;
  user_id: string;
  plan: PlanModel;
  status: string;
  p_status: number;
  buyer_data: ICreatedBy;
  public_start_date?: number;
  public_end_date?: number;
  images: Image[];
  buyer_id: string | null;
  plan_type?: number;
  total_adult?: number;
}

interface IHotelSearch {
  data_type: string;
  description: string;
  hotel_id: string;
  kana_name: string;
  hotel_keyword: string[];
  logo: string;
  name: string;
  rate: string;
  hotel_zone_code: {
    area: string;
    city: string;
    zone: string;
  };
  hotel_zone_text: {
    area: string;
    city: string;
    zone: string;
  };
  spots: string[];
  stations: string[];
}

interface IMetadataItem {
  data_type: string;
  m_key: string;
  sort: string;
  text_value: string;
  count?: string;
  highlight?: {
    text_value: string[];
  };
  parent_path?: string;
  root_path?: string;
}

interface ItemZone {
  m_key: string;
  sort: string;
  text_value: string;
  order: number;
  areas?: ItemArea[];
  checked: boolean;
}

interface ItemArea {
  m_key: string;
  sort: string;
  text_value: string;
  root_path: string;
  order: number;
  citys?: ItemCity[];
  checked: boolean;
}

interface ItemCity {
  m_key: string;
  sort: string;
  text_value: string;
  root_path: string;
  parent_path: string;
  order: number;
  checked: boolean;
}

interface TagInputSearch {
  id: string;
  label: string;
  type: TagInputSearchEnum;
  metadata?: any;
}

interface OtherFilterItem {
  m_key: string;
  sort: string;
  text_value: string;
  root_path: string;
  parent_path: string;
}
interface INafutelPagination {
  total: number;
  size: number;
  page: number;
}

interface FormUserReviewData {
  comment: string;
  amenity: number;
  facilities: number;
  customer_service: number;
  bath: number;
  satisfaction: number;
}

interface RoomInfoPerson {
  adult: number;
  childrens: number;
  upper_student_upper?: number;
  lower_student_upper?: number;
  infants_1?: number;
  infants_2?: number;
  infants_3?: number;
  infants_4?: number;
}

interface FormInfoBankUser {
  bank_code: string;
  bank_name: string;
  bank_branch: string;
  bank_number: string;
  bank_holder: string;
}

interface FormWithDrawUser {
  amount: number;
}

enum RoomPlanSortType {
  BY_CHEAPEST = "price_cheapest",
  BY_EXPENSIVE = "price_expensive",
  BY_ORDER = "order",
}

// NOTE: MUST maping via QUERY_STRING_......(file defineQueryStringProperties.ts)
enum TagInputSearchEnum {
  ZONE = "zid",
  AREA = "aid",
  CITY = "cid",
  STATION = "hotel_station",
  SPOTS = "hotel_tourist_spot",
  AMENITY = "room_amenity",
  FACILITY = "room_facility",
  HOTEL_TYPE = "hotel_type",
  ROOM_TYPE = "room_type",
  ROOM_BED_TYPE = "bed_type",
  ROOM_SPRING_TYPE = "spring_type",
  ROOM_BATH_TYPE = "bath_type",
  HOTEL_THEME = "hotel_theme",
  SERVICE_TYPE = "service_type",
  VIEW_TYPE = "view_type",
  LATE_CHECKOUT = "late_checkout",
  BENIFIT = "benifit",
  EVALUTION = "evalution",
  INFO_PET = "hotel_info_pet",
  INFO_SPECIAL = "hotel_info_special",
  BUDGET_FROM = "budget_from",
  BUDGET_TO = "budget_to",
  ROOM_STYLE = "room_style",
  SMOKING_TYPE = "smoking_type",
  METHOD_PAYMENT_SUPPORT = "method_payment_support",
  HOTEL_AMENITY = "hotel_amenity",
  HOTEL_FACILITY = "hotel_facility",
}

enum EReservationType {
  RESERVE = "reserve",
  RESALE = "resale",
}

enum ERROR_USER_MESSAGE {
  OLD_PASSWORD_INCORRECT = "OLD_PASSWORD_INCORRECT",
  BALANCE_NOT_ENOUGH = "BALANCE_NOT_ENOUGH",
  POINT_INVALID = "POINT_INVALID",
  CARD_INVALID = "CARD_INVALID",
}

enum ERROR_CARD_DETAIL {
  NOT_FOUND_RESULT = "NOT_FOUND_RESULT",
  CHARGE_ERROR = "CHARGE_ERROR",
  H10000 = "H10000",
}

enum PATH_URL {
  HOME = "/",
  // reservation
  HOTEL_LIST = "/hotel/list",
  HOTEL_DETAIL = "/hotel/detail",
  ROOM_DETAIL = "/hotel/room-detail",
  RESERVATION_LIST = "/authed/hotel/reservation-list",
  RESERVATION_DETAIL = "/authed/hotel/reservation/detail",
  RESERVATION_MY_PAGE = "/authed/hotel/my-page",
  RESERVATION_INPUT = "/hotel/reservation-input",
  RESERVATION_INPUT_SUCCESS = "/hotel/reservation-input/success",

  // resale
  RESALE_HOME = "/resale/top",
  RESALE_LIST = "/resale/list-during",
  RESALE_DETAIL = "/resale/detail",
  RESALE_RESERVATION_LIST = "/authed/resale/reservation-list",
  RESALE_RESERVATION_DETAIL = "/authed/resale/reservation/detail",
  RESALE_MY_PAGE = "/authed/resale/my-page",
  RESALE_RESERVATION_INPUT = "/resale/reservation-input",
  RESALE_RESERVATION_INPUT_SUCCESS = "/resale/reservation-input/success",
  RESALE_PUBLIC = "/authed/resale/to-form-public",

  // user
  USER_PROFILE = "/authed/my-page/user-profile",

  // sale management
  SALE_MANAGEMENT = "/authed/my-page/sale-management",
}

export type {
  IUser,
  IAuth,
  IRoute,
  IPermissionsResult,
  HotelModel,
  RoomModel,
  PriceModel,
  PlanModel,
  FormHotelReservationModel,
  IColumn,
  IRegion,
  IProvince,
  IStation,
  RoomDetailHotelSevervationModel,
  OrderDetailModel,
  IRoomTag,
  IOrderRoomDetail,
  ReservationListitemModel,
  ReservationDetailModel,
  IServerResponse,
  IHotelType,
  IAmenities,
  IHotelIntroduction,
  INotice,
  IReview,
  IResaleHotelItem,
  ReSaleDetailModel,
  FormToSaleModel,
  IMetaItem,
  IResaleHotelModel,
  ICreatedBy,
  IResaleReservation,
  NotificationModel,
  IHotelSearch,
  IMetadataItem,
  ItemZone,
  ItemArea,
  ItemCity,
  TagInputSearch,
  IMetaItemDynamoDb,
  IMetaItemPopularFilter,
  IMetaItemPopularKeyword,
  OtherFilterItem,
  INafutelPagination,
  RoomInfoPerson,
  FormChangeQuantityGuest,
  FormCardPaymentReservationModel,
  FormUserProfile,
  FormChangePasswordData,
  FormUserReviewData,
  UserReview,
  FormInfoBankUser,
  FormWithDrawUser,
};

export {
  ROLES,
  ELanguage,
  IStatusCode,
  ELocalStorageKey,
  EResaleOrderStatus,
  EReservationStatus,
  PATH_URL,
  EReservationType,
  TagInputSearchEnum,
  ESortBy,
  RoomPlanSortType,
  ERROR_USER_MESSAGE,
  FLOW_PAYMENT_ENUM,
  ERROR_CARD_DETAIL,
};
